import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import axios from 'axios';
import { getFileUrl } from '../config/api';

interface Caregiver {
  _id: string;
  firstName: string;
  lastName: string;
  profilePhoto: string;
  servicesOffered: string[];
  yearsOfExperience: number;
  hourlyRate?: { min: number; max: number };
  rating: { average: number; count: number };
  isVerified: boolean;
  areaOfService?: {
    city: string;
    address: string;
    serviceRadius?: number;
  };
}

const FindCaregivers: React.FC = () => {
  usePageTitle('Find Caregivers');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const serviceType = searchParams.get('serviceType') || '';
  
  const [caregivers, setCaregivers] = useState<Caregiver[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);

  const serviceTypeNames: Record<string, string> = {
    'nurse-care': 'Nurse Care',
    'everyday-assistance': 'Everyday Assistance'
  };

  useEffect(() => {
    // If no service type selected, do not fetch caregivers
    if (!serviceType) {
      setLoading(false);
      setCaregivers([]);
      return;
    }

    const fetchCaregivers = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        let url = `/api/caregivers/search?serviceType=${serviceType}`;
        
        if (startDate && endDate) {
          url += `&startDate=${startDate}&endDate=${endDate}`;
        }
        
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCaregivers(response.data.data || []);
      } catch (error: any) {
        console.error('Error fetching caregivers:', error);
        setError('Failed to load caregivers');
      } finally {
        setLoading(false);
      }
    };

    fetchCaregivers();
  }, [serviceType, startDate, endDate]);

  const handleServiceSelect = (type: string) => {
    navigate(`/customer/find-caregivers?serviceType=${type}`);
  };

  if (!serviceType) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
        
        <div className="flex-grow max-w-7xl mx-auto px-4 py-12 w-full pb-24 sm:pb-12 flex flex-col items-center justify-center min-h-[80vh] relative">
          <button
            onClick={() => navigate('/customer/my-requests')}
            className="absolute top-6 left-4 sm:left-8 flex items-center text-gray-600 hover:text-gray-900 transition-colors group z-10"
          >
            <div className="w-10 h-10 rounded-full bg-white shadow-sm border border-gray-100 group-hover:bg-gray-50 flex items-center justify-center mr-3 transition-colors">
               <svg className="w-5 h-5 text-gray-600 group-hover:text-primary-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
               </svg>
            </div>
            <span className="font-medium text-gray-700 group-hover:text-gray-900">Back</span>
          </button>

          <div className="w-full mb-12 text-center max-w-2xl mx-auto pt-12 sm:pt-0">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">How can we help you today?</h2>
            <p className="text-lg text-gray-500 leading-relaxed">Choose the type of care service that best matches your loved one's needs.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 w-full max-w-5xl px-4">
            {/* Nurse Care Card */}
            <button
              onClick={() => handleServiceSelect('nurse-care')}
              className="relative group bg-white rounded-3xl p-8 shadow-sm hover:shadow-2xl border border-gray-100 hover:border-primary-100 transition-all duration-300 transform hover:-translate-y-1 text-center flex flex-col items-center overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -mr-10 -mt-10 transition-transform group-hover:scale-110"></div>
              
              <div className="relative z-10 w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mb-6 text-blue-600 shadow-sm group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">Nurse Care</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Professional medical assistance including medication management, vitals monitoring, wound care, and specialized health support from certified nurses.
              </p>
              
              <span className="inline-flex items-center justify-center text-sm font-bold text-blue-600 group-hover:translate-x-1 transition-transform">
                Find Nurses <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </span>
            </button>

            {/* Everyday Assistance Card */}
            <button
              onClick={() => handleServiceSelect('everyday-assistance')}
              className="relative group bg-white rounded-3xl p-8 shadow-sm hover:shadow-2xl border border-gray-100 hover:border-green-100 transition-all duration-300 transform hover:-translate-y-1 text-center flex flex-col items-center overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-50 rounded-bl-full -mr-10 -mt-10 transition-transform group-hover:scale-110"></div>

              <div className="relative z-10 w-20 h-20 bg-green-100 rounded-2xl flex items-center justify-center mb-6 text-green-600 shadow-sm group-hover:bg-green-600 group-hover:text-white transition-colors duration-300">
                 <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors">Everyday Assistance</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Compassionate support with daily living activities, including housekeeping, meal preparation, mobility assistance, and companionship.
              </p>

              <span className="inline-flex items-center justify-center text-sm font-bold text-green-600 group-hover:translate-x-1 transition-transform">
                Find Assistants <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans animate-fade-in">
      
      {/* Main Content */}
      <div className="flex-grow max-w-7xl mx-auto px-4 py-8 w-full pb-20 sm:pb-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
             <button
                onClick={() => navigate(-1)}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors group"
             >
               <div className="w-10 h-10 rounded-full bg-white shadow-sm border border-gray-100 group-hover:bg-gray-50 flex items-center justify-center mr-3 transition-colors">
                  <svg className="w-5 h-5 text-gray-600 group-hover:text-primary-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
               </div>
               <span className="font-medium text-gray-700 group-hover:text-gray-900">Back</span>
             </button>

              {/* Filter Button (Desktop & Mobile) */}
               <button
                  onClick={() => setShowFilterModal(true)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border shadow-sm hover:shadow transition-all ${
                     startDate && endDate 
                     ? 'bg-primary-50 border-primary-200 text-primary-700' 
                     : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  <span className="font-medium text-sm hidden sm:inline">Filter Availability</span>
                  {startDate && endDate && (
                    <span className="ml-1 bg-primary-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">1</span>
                  )}
                </button>
          </div>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-primary-600 uppercase tracking-wide mb-1">
                 {serviceType === 'nurse-care' ? 'Medical Professionals' : 'Support Assistants'}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                {serviceTypeNames[serviceType] || 'Find Caregivers'}
              </h2>
            </div>
            {caregivers.length > 0 && (
                <p className="text-gray-500 font-medium">{caregivers.length} verified professionals available</p>
            )}
          </div>
        </div>

        {/* Active Filter Badge */}
        {startDate && endDate && (
          <div className="mb-8 flex items-center gap-3 animate-fade-in">
            <div className="inline-flex items-center gap-2 bg-white border border-primary-100 text-primary-700 rounded-full pl-4 pr-1 py-1 shadow-sm">
              <span className="text-sm font-medium">
                Dates: <span className="font-bold">{new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              </span>
              <button
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                }}
                className="bg-primary-50 p-1.5 rounded-full hover:bg-primary-100 text-primary-600 transition-colors"
                title="Clear filter"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-600 border-t-transparent mb-4"></div>
            <p className="text-gray-500 font-medium">Finding the best matches...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-100 text-red-700 px-6 py-4 rounded-xl flex items-center gap-3">
             <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
             <span className="font-medium">{error}</span>
          </div>
        ) : caregivers.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-3xl border border-gray-100 shadow-sm px-4">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-50 rounded-full mb-6">
              {/* Using a custom empty state SVG or standard icon */}
              <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">No caregivers found</h3>
            <p className="text-gray-500 max-w-md mx-auto mb-8 leading-relaxed">We couldn't find any caregivers matching your criteria. Try adjusting your dates or check back later.</p>
            <button 
               onClick={() => { setStartDate(''); setEndDate(''); }}
               className="px-6 py-2.5 bg-gray-900 text-white rounded-lg font-medium hover:bg-black transition-colors"
            >
               Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {caregivers.map((caregiver) => (
              <div
                key={caregiver._id}
                className="group bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-xl hover:border-primary-100 transition-all duration-300 flex flex-col items-center text-center animate-slide-up hover:scale-[1.02]"
              >
                {/* Header */}
                <div className="flex flex-col items-center gap-2 mb-3 w-full">
                  <div className="relative">
                     {caregiver.profilePhoto ? (
                        <img
                           src={getFileUrl(caregiver.profilePhoto)}
                           alt={`${caregiver.firstName} ${caregiver.lastName}`}
                           className="w-16 h-16 rounded-2xl object-cover shadow-sm group-hover:scale-105 transition-transform duration-300"
                           onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                     ) : (
                        <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-primary-50 rounded-2xl flex items-center justify-center shadow-inner">
                           <span className="text-xl font-bold text-primary-600">
                           {caregiver.firstName?.charAt(0)}{caregiver.lastName?.charAt(0)}
                           </span>
                        </div>
                     )}
                     {caregiver.isVerified && (
                        <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-1 shadow-sm" title="Verified Pro">
                           <div className="bg-blue-500 rounded-full p-0.5">
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                           </div>
                        </div>
                     )}
                  </div>
                  
                  <div className="w-full min-w-0">
                    <h3 className="text-lg font-bold text-gray-900 truncate group-hover:text-primary-700 transition-colors">
                      {caregiver.firstName} {caregiver.lastName}
                    </h3>
                    <div className="flex items-center justify-center gap-1.5 mt-0.5">
                      <svg className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      <span className="text-sm font-semibold text-gray-900">{caregiver.rating.average.toFixed(1)}</span>
                      <span className="text-xs text-gray-500 font-medium">({caregiver.rating.count} reviews)</span>
                    </div>
                  </div>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-2 mb-3 w-full">
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                     <p className="text-[10px] text-gray-500 uppercase tracking-wide font-medium mb-0.5">Experience</p>
                     <p className="text-sm font-semibold text-gray-900">{caregiver.yearsOfExperience}+ Years</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                     <p className="text-[10px] text-gray-500 uppercase tracking-wide font-medium mb-0.5">Location</p>
                     <p className="text-sm font-semibold text-gray-900 truncate">{caregiver.areaOfService?.city || 'N/A'}</p>
                  </div>
                </div>

                {/* Rate Section */}
                <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-center w-full gap-4">
                  <button
                    onClick={() => navigate(`/caregiver/${caregiver._id}`)}
                    className="w-full px-5 py-2 bg-gray-900 text-white rounded-xl font-medium text-sm hover:bg-primary-600 transition-all shadow-sm hover:shadow-md"
                  >
                    View & Book
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="hidden sm:block bg-gray-900 text-white py-12 px-4 mt-auto">
        <div className="max-w-7xl mx-auto text-center">
          <h4 className="text-2xl font-bold font-proyas tracking-wider mb-2">PROYAS</h4>
          <p className="text-gray-400 text-sm mb-8">Quality care, delivered with compassion</p>
          
          <div className="flex justify-center items-center gap-8 mb-8">
            <a href="mailto:contact@proyas.net" className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors">
              <span className="p-2 bg-gray-800 rounded-full">
                 <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                </svg>
              </span>
              <span className="text-sm font-medium">contact@proyas.net</span>
            </a>
            <a href="https://www.facebook.com/profile.php?id=61586633764841" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors">
              <span className="p-2 bg-gray-800 rounded-full">
                 <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </span>
              <span className="text-sm font-medium">Facebook</span>
            </a>
          </div>
          
          <div className="pt-8 border-t border-gray-800">
             <p className="text-gray-500 text-xs">© 2026 <span className="font-proyas tracking-wider text-gray-400">PROYAS</span>. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setShowFilterModal(false)}>
          <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-w-md w-full p-6 animate-slide-up sm:animate-zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-8">
              <div>
                 <h3 className="text-2xl font-bold text-gray-900">Check Availability</h3>
                 <p className="text-sm text-gray-500 mt-1">Filter caregivers by your schedule</p>
              </div>
              <button
                onClick={() => setShowFilterModal(false)}
                className="w-10 h-10 bg-gray-50 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-5">
              <div className="relative">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block ml-1">
                  From
                </label>
                <div className="relative">
                   <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                     <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                     </svg>
                   </div>
                   <input
                     type="date"
                     value={startDate}
                     onChange={(e) => setStartDate(e.target.value)}
                     min={new Date().toISOString().split('T')[0]}
                     className="block w-full pl-10 pr-4 py-3 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all font-medium text-gray-900"
                   />
                </div>
              </div>

              <div className="relative">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 block ml-1">
                  To
                </label>
                <div className="relative">
                   <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                     <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                     </svg>
                   </div>
                   <input
                     type="date"
                     value={endDate}
                     onChange={(e) => setEndDate(e.target.value)}
                     min={startDate || new Date().toISOString().split('T')[0]}
                     className="block w-full pl-10 pr-4 py-3 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all font-medium text-gray-900"
                   />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-8 pt-4 border-t border-gray-100">
              <button
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setShowFilterModal(false);
                }}
                className="flex-1 px-4 py-3.5 bg-white border border-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-50 transition-colors"
              >
                Reset
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                className="flex-1 px-4 py-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold shadow-lg shadow-primary-200 transition-all transform active:scale-95"
              >
                Show Results
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default FindCaregivers;

// Verified
